import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList,
  Loader2, RefreshCw, ShieldAlert, ShoppingCart, Lock,
  BarChart2, AlertTriangle, Info,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ──────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Dry-run Bridge' },
  { id: 3, label: 'Review Rows' },
  { id: 4, label: 'Apply' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toFixed(2)}`;
}

function fmtQty(v, places = 4) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toFixed(places);
}

function fmtQty2(v) { return fmtQty(v, 2); }

// ── Step nav ──────────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s4">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s4-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cls = {
    planned:    'bg-amber-50 text-amber-700 border-amber-200',
    applied:    'bg-emerald-50 text-emerald-700 border-emerald-200',
    importable: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    held:       'bg-red-50 text-red-700 border-red-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <Badge variant="outline" className={`text-xs font-medium ${cls}`}>
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Source fingerprint banner ─────────────────────────────────────────────────

function SourceBanner({ session }) {
  if (!session) return null;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 mb-4"
      data-testid="s4-source-banner"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
        <span><span className="font-semibold">Session:</span> {session.session_reference}</span>
        <span><span className="font-semibold">Route:</span> B — Historic Draft</span>
        <span><span className="font-semibold">Site:</span> {session.target_site_id}</span>
        <span><span className="font-semibold">Period:</span> {session.period_from} → {session.period_to}</span>
        <span><span className="font-semibold">Stage 0:</span> <span className="font-mono">{session.stage0_run_id?.slice(0, 12)}…</span></span>
        <span><span className="font-semibold">S1 Run:</span> <span className="font-mono">{session.s1_run_id?.slice(0, 8)}…</span></span>
      </div>
    </div>
  );
}

// ── Bridge panel — 3-pillar + supplementary ───────────────────────────────────

function BridgePillar({ label, source, importable, held, fmt = fmtQty2, unit = '', testIdPrefix }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">{label}</div>
      <div className="grid grid-cols-3 gap-1 text-center">
        {[
          { key: 'source',     v: source,     label: 'Source',     color: 'text-slate-800' },
          { key: 'importable', v: importable, label: 'Importable', color: 'text-emerald-700' },
          { key: 'held',       v: held,       label: 'Held',       color: 'text-red-600' },
        ].map(col => (
          <div key={col.key} className="bg-slate-50 rounded p-1.5">
            <div className="text-[10px] text-slate-400 mb-0.5">{col.label}</div>
            <div className={`text-sm font-bold ${col.color}`} data-testid={testIdPrefix ? `${testIdPrefix}-${col.key}` : undefined}>
              {fmt(col.v)}{unit && col.v != null ? unit : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BridgePanel({ bridge, planStatus }) {
  if (!bridge) return null;
  const b = typeof bridge === 'string' ? JSON.parse(bridge) : bridge;
  const ok = b.fully_reconciles === true || b.fully_reconciles === 'true';

  return (
    <div
      data-testid="s4-bridge-panel"
      className={`rounded-lg border p-4 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-slate-700">Sales Bridge — 3 Pillars</span>
        <div className="flex gap-2">
          {ok
            ? <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">Fully Reconciles</Badge>
            : <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">Mismatch</Badge>
          }
          {planStatus === 'applied' && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Applied</Badge>
          )}
        </div>
      </div>

      {/* Three reconciliation pillars */}
      <div className="flex gap-3 mb-4" data-testid="s4-bridge-pillars">
        <BridgePillar
          label="Row Count"
          source={b.source_rows}
          importable={b.importable_rows}
          held={b.held_rows}
          fmt={v => v ?? '—'}
          testIdPrefix="s4-rows"
        />
        <BridgePillar
          label="PLU Quantity (TILLTL)"
          source={b.source_till_total}
          importable={b.importable_till_total}
          held={b.held_till_total}
          fmt={fmtQty2}
          testIdPrefix="s4-till"
        />
        <BridgePillar
          label="Legacy Value (BARPRICE × TillTotal)"
          source={b.source_legacy_value}
          importable={b.importable_legacy_value}
          held={b.held_legacy_value}
          fmt={fmtGBP}
          testIdPrefix="s4-legval"
        />
      </div>

      {/* Reconciliation status */}
      <div className="flex gap-3 text-xs mb-4">
        {[
          { label: 'Row Count', ok: b.rows_reconcile },
          { label: 'PLU Qty', ok: b.till_qty_reconciles },
          { label: 'Value', ok: b.value_reconciles },
        ].map(p => (
          <div key={p.label} className={`flex items-center gap-1 px-2 py-0.5 rounded ${p.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {p.ok ? <CheckCircle2 size={11} /> : <ShieldAlert size={11} />}
            {p.label}
          </div>
        ))}
      </div>

      {/* Target / AbbeyZone proposed value */}
      <div className="rounded-md border border-slate-200 bg-white p-3 mb-3">
        <div className="text-xs font-semibold text-slate-700 mb-2">Target Value (AbbeyZone Site Price — Importable Only)</div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div>
            <div className="text-slate-400">Importable Legacy</div>
            <div className="font-bold text-slate-800" data-testid="s4-imp-legacy">{fmtGBP(b.importable_legacy_value)}</div>
            <div className="text-[10px] text-slate-400">BARPRICE × TillTotal</div>
          </div>
          <div>
            <div className="text-slate-400">Target Proposed</div>
            <div className="font-bold text-emerald-700" data-testid="s4-imp-target">{fmtGBP(b.importable_target_value)}</div>
            <div className="text-[10px] text-slate-400">Site Price × TillTotal</div>
          </div>
          <div>
            <div className="text-slate-400">Value Variance</div>
            <div className={`font-bold ${parseFloat(b.value_variance || 0) < 0 ? 'text-red-600' : parseFloat(b.value_variance || 0) > 0 ? 'text-blue-600' : 'text-slate-800'}`} data-testid="s4-val-variance">
              {fmtGBP(b.value_variance)}
            </div>
            <div className="text-[10px] text-slate-400">Target − Legacy</div>
          </div>
        </div>
      </div>

      {/* Supplementary: allowances + stock consumption */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
          <div className="font-semibold text-amber-800 mb-1">Allowances (held for Stage 6)</div>
          <div className="flex justify-between">
            <span className="text-amber-700">Source: {fmtQty2(b.source_allowance)}</span>
            <span className="text-amber-700">Importable: {fmtQty2(b.importable_allowance)}</span>
            <span className="text-amber-700">Held: {fmtQty2(b.held_allowance)}</span>
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
          <div className="font-semibold text-slate-700 mb-1">Stock Consumption (TRANFILE.SALE × Yield)</div>
          <div className="flex justify-between">
            <span className="text-slate-600">Source: {fmtQty(b.source_tran_x_yield, 4)}</span>
            <span className="text-slate-600">Imp: {fmtQty(b.importable_tran_x_yield, 4)}</span>
            <span className="text-slate-600">Held: {fmtQty(b.held_tran_x_yield, 4)}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-2 border-t border-slate-200 text-xs text-slate-500 text-center">
        Source {fmtGBP(b.source_legacy_value)} = Importable {fmtGBP(b.importable_legacy_value)} + Held {fmtGBP(b.held_legacy_value)}
        {ok ? '  ✓ reconciles' : '  ← mismatch'}
      </div>
    </div>
  );
}

// ── Recalc status badge ────────────────────────────────────────────────────────

function RecalcBadge({ stable, sha1, sha2 }) {
  if (stable === null || stable === undefined) return null;
  const ok = stable === true || stable === 'true';
  return (
    <div
      className={`rounded-md border p-2 text-xs ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}
      data-testid="s4-recalc-status"
    >
      <div className="flex items-center gap-1.5 mb-1">
        {ok ? <CheckCircle2 size={13} /> : <ShieldAlert size={13} />}
        <span className="font-semibold">{ok ? 'Recalculation Stable' : 'Recalculation Unstable'}</span>
      </div>
      <div className="font-mono text-[10px] space-y-0.5">
        <div>Recalc 1: {sha1?.slice(0, 16)}</div>
        <div>Recalc 2: {sha2?.slice(0, 16)}</div>
        <div className={ok ? 'text-emerald-600' : 'text-red-600'}>
          {sha1 === sha2 ? '✓ SHA match — computation deterministic' : '✗ SHA mismatch — computation non-deterministic'}
        </div>
      </div>
    </div>
  );
}

// ── PLU rows table ────────────────────────────────────────────────────────────

const ROW_TABS = [
  { key: 'all',        label: 'All',        icon: null },
  { key: 'importable', label: 'Importable', icon: <CheckCircle2 size={12} /> },
  { key: 'held',       label: 'Held',       icon: <Lock size={12} /> },
];

function PluRowsTable({ planId, category, setCategory }) {
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const PAGE_SIZE = 25;

  const load = useCallback(async (cat, pg) => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage4/plans/${planId}?category=${cat}&page=${pg}&page_size=${PAGE_SIZE}`
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load PLU rows');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    setPage(1);
    load(category, 1);
  }, [category, planId, load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="s4-plu-rows-table">
      <div className="flex gap-2 mb-3 flex-wrap">
        {ROW_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`s4-rows-tab-${tab.key}`}
            onClick={() => setCategory(tab.key)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors
              ${category === tab.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
        {loading && <Loader2 size={14} className="animate-spin text-slate-400 self-center ml-1" />}
        <span className="self-center ml-auto text-xs text-slate-400">{total} rows</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs min-w-[1100px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wide">
              <th className="py-2 px-3 font-semibold">PLU</th>
              <th className="py-2 px-3 font-semibold">Product</th>
              <th className="py-2 px-3 font-semibold text-right">TillTotal</th>
              <th className="py-2 px-3 font-semibold text-right">Allow</th>
              <th className="py-2 px-3 font-semibold text-right">BARPRICE</th>
              <th className="py-2 px-3 font-semibold text-right">Source Val</th>
              <th className="py-2 px-3 font-semibold">Measure</th>
              <th className="py-2 px-3 font-semibold text-right">Yield</th>
              <th className="py-2 px-3 font-semibold text-right">TRAN×Yield</th>
              <th className="py-2 px-3 font-semibold text-right">Qty Var</th>
              <th className="py-2 px-3 font-semibold text-right">Site Price</th>
              <th className="py-2 px-3 font-semibold text-right">Target Val</th>
              <th className="py-2 px-3 font-semibold text-right">Val Var</th>
              <th className="py-2 px-3 font-semibold">Status</th>
              <th className="py-2 px-3 font-semibold">Hold Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr><td colSpan="15" className="py-8 text-center text-slate-400">No rows</td></tr>
            )}
            {rows.map(r => {
              const isImportable = r.resolution_status === 'importable';
              const isExpanded = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    data-testid={`s4-row-${r.plu_code}`}
                    className={`transition-colors cursor-pointer ${isImportable ? 'hover:bg-emerald-50' : 'hover:bg-slate-50 opacity-70'}`}
                    onClick={() => setExpanded(isExpanded ? null : r.id)}
                  >
                    <td className="py-1.5 px-3 font-mono text-slate-800 font-semibold">{r.plu_code}</td>
                    <td className="py-1.5 px-3 text-slate-600 max-w-[140px] truncate">{r.bp_name_snapshot || '—'}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-slate-800">{fmtQty2(r.till_total)}</td>
                    <td className={`py-1.5 px-3 text-right font-mono ${parseFloat(r.allowance || 0) !== 0 ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                      {fmtQty2(r.allowance)}
                    </td>
                    <td className="py-1.5 px-3 text-right text-slate-600">{fmtGBP(r.legacy_barprice)}</td>
                    <td className="py-1.5 px-3 text-right font-medium text-slate-800">{fmtGBP(r.source_value)}</td>
                    <td className="py-1.5 px-3 text-slate-500">
                      {r.measure_label || (r.measure_ml ? `${r.measure_ml}ml` : '—')}
                    </td>
                    <td className="py-1.5 px-3 text-right text-slate-500">{r.legacy_yield ? parseFloat(r.legacy_yield).toFixed(1) : '—'}</td>
                    <td className="py-1.5 px-3 text-right text-slate-500">{r.tran_sale_x_yield ? parseFloat(r.tran_sale_x_yield).toFixed(2) : '—'}</td>
                    <td className={`py-1.5 px-3 text-right font-mono ${parseFloat(r.qty_variance || 0) < 0 ? 'text-red-600' : parseFloat(r.qty_variance || 0) > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                      {r.qty_variance != null ? parseFloat(r.qty_variance).toFixed(2) : '—'}
                    </td>
                    <td className="py-1.5 px-3 text-right text-slate-600">{isImportable ? fmtGBP(r.site_selling_price) : '—'}</td>
                    <td className="py-1.5 px-3 text-right font-medium text-emerald-700">{isImportable ? fmtGBP(r.target_value) : '—'}</td>
                    <td className={`py-1.5 px-3 text-right font-mono ${parseFloat(r.value_variance || 0) < 0 ? 'text-red-600' : parseFloat(r.value_variance || 0) > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                      {isImportable && r.value_variance != null ? fmtGBP(r.value_variance) : '—'}
                    </td>
                    <td className="py-1.5 px-3"><StatusBadge status={r.resolution_status} /></td>
                    <td className="py-1.5 px-3 text-slate-400 text-xs max-w-[140px] truncate" title={r.hold_reason || ''}>
                      {r.hold_reason || '—'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr
                      data-testid={`s4-row-${r.plu_code}-expanded`}
                      className="bg-slate-50"
                    >
                      <td colSpan="15" className="px-4 py-2">
                        <div className="text-xs grid grid-cols-3 sm:grid-cols-6 gap-3">
                          {['till1','till2','till3','till4','till5','till6'].map(k => (
                            <div key={k} className="text-center">
                              <div className="text-slate-400 capitalize">{k.replace('till', 'Till ')}</div>
                              <div
                                data-testid={`s4-row-${r.plu_code}-${k}`}
                                className={`font-mono font-semibold ${parseFloat(r[k]||0) > 0 ? 'text-slate-800' : 'text-slate-300'}`}
                              >
                                {fmtQty2(r[k])}
                              </div>
                            </div>
                          ))}
                          <div className="text-center col-span-1">
                            <div className="text-slate-400">TRAN SALE</div>
                            <div
                              data-testid={`s4-row-${r.plu_code}-tran-sale`}
                              className="font-mono text-slate-600"
                            >
                              {r.tran_sale_qty ? parseFloat(r.tran_sale_qty).toFixed(4) : '—'}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-slate-400">Container ml</div>
                            <div
                              data-testid={`s4-row-${r.plu_code}-container-ml`}
                              className="font-mono text-slate-600"
                            >
                              {r.container_ml ? parseFloat(r.container_ml).toFixed(0) : '—'}
                            </div>
                          </div>
                          {r.created_sale_line_id && (
                            <div className="text-center">
                              <div className="text-slate-400">Sale Line ID</div>
                              <div className="font-mono text-emerald-700">#{r.created_sale_line_id}</div>
                            </div>
                          )}
                          {r.source_row_sha256 && (
                            <div className="col-span-3 text-center">
                              <div className="text-slate-400">Source SHA256</div>
                              <div className="font-mono text-[10px] text-slate-500">{r.source_row_sha256.slice(0, 24)}…</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{total} total rows</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1}
              onClick={() => { const p = page - 1; setPage(p); load(category, p); }}>
              Prev
            </Button>
            <span className="self-center">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => { const p = page + 1; setPage(p); load(category, p); }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Applied state panel ───────────────────────────────────────────────────────

function AppliedPanel({ applyResult }) {
  if (!applyResult) return null;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="s4-applied-panel">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 size={18} className="text-emerald-600" />
        <span className="font-semibold text-emerald-800">
          {applyResult.idempotent ? 'Already Applied (Idempotent)' : 'Stage 4 Applied Successfully'}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-white rounded-md p-2 border border-emerald-100">
          <div className="text-slate-500">Sale ID</div>
          <div className="font-bold text-slate-800" data-testid="s4-sale-id">#{applyResult.sale_id}</div>
        </div>
        <div className="bg-white rounded-md p-2 border border-emerald-100">
          <div className="text-slate-500">Lines Created</div>
          <div className="font-bold text-slate-800" data-testid="s4-lines-created">{applyResult.lines_created}</div>
        </div>
        <div className="bg-white rounded-md p-2 border border-emerald-100">
          <div className="text-slate-500">Lines Total</div>
          <div className="font-bold text-emerald-700" data-testid="s4-lines-total">{fmtGBP(applyResult.lines_total)}</div>
        </div>
        <div className="bg-white rounded-md p-2 border border-emerald-100">
          <div className="text-slate-500">Applied At</div>
          <div className="font-mono text-slate-600 text-[10px]">{applyResult.applied_at?.slice(0, 19)}</div>
        </div>
      </div>
      <div className="mt-3">
        <RecalcBadge
          stable={applyResult.recalc_stable}
          sha1={applyResult.recalc_1_sha}
          sha2={applyResult.recalc_2_sha}
        />
      </div>
      {applyResult.idempotent && (
        <div className="mt-2 text-xs text-emerald-700 text-center">
          Repeat apply — no new records created, same Sale #{applyResult.sale_id} returned.
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage4Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Step state
  const [step, setStep]             = useState(1);
  const [sessions, setSessions]     = useState([]);
  const [sessLoading, setSessLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // Plan state
  const [creating, setCreating]     = useState(false);
  const [plan, setPlan]             = useState(null);
  const [planId, setPlanId]         = useState(null);
  const [rowCategory, setRowCategory] = useState('all');

  // Recalc state
  const [recalcing, setRecalcing]   = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  // Apply state
  const [applying, setApplying]     = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const restoredRef = useRef(false);

  // ── Load sessions ──────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    setSessLoading(true);
    try {
      const res = await api.get('/admin/psb-stage4/s2-sessions');
      setSessions(res.data.sessions || []);
    } catch {
      toast.error('Failed to load Stage 2 sessions');
    } finally {
      setSessLoading(false);
    }
  }, []);

  // ── Restore plan from ?planId= URL param ──────────────────────────────────

  const restorePlan = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/admin/psb-stage4/plans/${id}?category=all&page=1&page_size=1`);
      const p = res.data.plan;
      if (!p) return;
      setPlan(p);
      setPlanId(p.run_id);
      // Find matching session
      setSessions(prev => {
        const sess = prev.find(s => s.s2_plan_id === p.s2_plan_id);
        if (sess) setSelectedSession(sess);
        return prev;
      });
      if (p.status === 'applied') {
        const saleInfo = res.data.sale_info || {};
        setApplyResult({
          idempotent: true,
          plan_id: p.run_id,
          sale_id: saleInfo.sale_id ?? '—',
          lines_created: p.bridge?.importable_rows ?? '—',
          lines_total: saleInfo.lines_total ?? p.bridge?.importable_target_value,
          recalc_stable: p.recalc_stable,
          recalc_1_sha: p.recalc_1_sha,
          recalc_2_sha: p.recalc_2_sha,
          applied_at: p.applied_at,
        });
        setStep(4);
      } else {
        setStep(2);
      }
    } catch (e) {
      // Plan not found — silently ignore (stale URL)
    }
  }, []);

  // ── Boot: load sessions, then restore URL plan ─────────────────────────────

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (restoredRef.current) return;
    if (sessLoading) return; // wait until sessions loaded to match session object
    const urlPlanId = searchParams.get('planId');
    if (urlPlanId && !plan) {
      restoredRef.current = true;
      restorePlan(urlPlanId);
    }
  }, [sessLoading, searchParams, plan, restorePlan]);

  // ── Sync ?planId= in URL ───────────────────────────────────────────────────

  useEffect(() => {
    if (planId && searchParams.get('planId') !== planId) {
      setSearchParams({ planId }, { replace: true });
    }
  }, [planId, setSearchParams, searchParams]);

  // ── Create plan ────────────────────────────────────────────────────────────

  const handleCreatePlan = useCallback(async () => {
    if (!selectedSession) return;
    setCreating(true);
    try {
      const res = await api.post('/admin/psb-stage4/plans', {
        stage0_run_id: selectedSession.stage0_run_id,
        s1_run_id: selectedSession.s1_run_id,
        s2_plan_id: selectedSession.s2_plan_id,
        target_session_id: selectedSession.target_session_id,
        site_id: selectedSession.target_site_id,
        notes: `Leicester Stage 4 — Session ${selectedSession.session_reference}`,
      });
      const p = res.data.plan;
      setPlan(p);
      setPlanId(p.run_id);
      if (res.data.idempotent) {
        toast.info('Existing plan restored (idempotent)');
      } else {
        toast.success('Dry-run plan created');
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create plan');
    } finally {
      setCreating(false);
    }
  }, [selectedSession]);

  // ── Recalculate ────────────────────────────────────────────────────────────

  const handleRecalculate = useCallback(async () => {
    if (!planId) return;
    setRecalcing(true);
    try {
      const res = await api.post(`/admin/psb-stage4/plans/${planId}/recalculate`);
      setRecalcResult(res.data);
      // Refresh plan metadata
      const planRes = await api.get(`/admin/psb-stage4/plans/${planId}?page=1&page_size=1`);
      setPlan(planRes.data.plan);
      if (res.data.stable) {
        toast.success('Recalculation stable — both runs match stored dry-run');
      } else {
        toast.warning('Recalculation unstable — review SHA comparison');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalcing(false);
    }
  }, [planId]);

  // ── Apply ──────────────────────────────────────────────────────────────────

  const handleApply = useCallback(async () => {
    if (!planId) return;
    if (!window.confirm(
      'Apply this Stage 4 plan?\n\n' +
      'This will create a Draft Sale with the importable PLU rows.\n' +
      'Double recalculation will run first. Allowed rows only (site 271). ' +
      'Repeat apply is idempotent.'
    )) return;
    setApplying(true);
    try {
      const res = await api.post(`/admin/psb-stage4/plans/${planId}/apply`);
      setApplyResult(res.data);
      // Refresh plan
      const planRes = await api.get(`/admin/psb-stage4/plans/${planId}?page=1&page_size=1`);
      setPlan(planRes.data.plan);
      if (res.data.idempotent) {
        toast.info('Already applied — same Sale returned (idempotent)');
      } else {
        toast.success(`Applied — Sale #${res.data.sale_id} created with ${res.data.lines_created} line(s)`);
      }
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [planId]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isApplied = plan?.status === 'applied';

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="psb-stage4-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/stocktaking/psb-stage3-import')}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="s4-back-btn"
            title="Back to Stage 3"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <ShoppingCart size={20} className="text-slate-600" />
              PSB Stage 4 — Sales / PLU Import
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Route B · Leicester Fixture · Site 271 · Session 120 (Draft)
            </p>
          </div>
        </div>
        {plan && (
          <div className="flex items-center gap-2">
            <StatusBadge status={plan.status} />
            <span className="text-xs text-slate-400 font-mono">
              {plan.run_id?.slice(0, 8)}
            </span>
          </div>
        )}
      </div>

      <StepNav current={step} />

      {/* ── Step 1: Source selection ─────────────────────────────────────── */}
      {step === 1 && (
        <Card data-testid="s4-step1-source">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList size={16} /> Select Applied Stage 2 Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-4">
              Select the applied Route B Stage 2 plan to use as the PLU source fingerprint.
              Stage 0 archive, Stage 1 approvals, and site are cross-checked server-side.
            </p>

            {sessLoading ? (
              <div className="flex items-center gap-2 text-slate-400 py-4">
                <Loader2 size={16} className="animate-spin" /> Loading sessions…
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-slate-400 text-sm py-4">
                No applied Route B Stage 2 sessions found for allowed sites.
              </div>
            ) : (
              <div className="space-y-2" data-testid="s4-session-list">
                {sessions.map(s => {
                  const isSelected = selectedSession?.s2_plan_id === s.s2_plan_id;
                  return (
                    <div
                      key={s.s2_plan_id}
                      data-testid={`s4-session-${s.s2_plan_id.slice(0, 8)}`}
                      onClick={() => setSelectedSession(s)}
                      className={`rounded-lg border p-3 cursor-pointer transition-all ${isSelected ? 'border-slate-800 bg-slate-50 ring-1 ring-slate-800' : 'border-slate-200 hover:border-slate-400'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            Session {s.session_reference}
                            <span className="ml-2 text-xs font-normal text-slate-400">
                              (ID {s.target_session_id})
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Site {s.target_site_id} · {s.period_from} → {s.period_to}
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-400 font-mono">
                          <div>S2: {s.s2_plan_id.slice(0, 8)}</div>
                          <div>S0: {s.stage0_run_id.slice(0, 8)}</div>
                          <div>S1: {s.s1_run_id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4">
              <Button
                data-testid="s4-build-plan-btn"
                disabled={!selectedSession || creating}
                onClick={handleCreatePlan}
                className="w-full sm:w-auto"
              >
                {creating
                  ? <><Loader2 size={14} className="animate-spin mr-2" />Building Plan…</>
                  : 'Build Dry-Run Plan'
                }
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Bridge ──────────────────────────────────────────────── */}
      {step >= 2 && plan && (
        <Card data-testid="s4-step2-bridge">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart2 size={16} /> Dry-Run Bridge
              {step === 2 && <Badge variant="outline" className="text-xs ml-2">Current Step</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SourceBanner session={selectedSession || {
              session_reference: `Session ${plan.target_session_id}`,
              target_site_id: plan.target_site_id,
              period_from: '—', period_to: '—',
              stage0_run_id: plan.stage0_run_id,
              s1_run_id: plan.s1_run_id,
            }} />
            <BridgePanel bridge={plan.bridge} planStatus={plan.status} />

            {/* Recalculation */}
            {!isApplied && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="s4-recalculate-btn"
                  onClick={handleRecalculate}
                  disabled={recalcing}
                >
                  {recalcing
                    ? <><Loader2 size={13} className="animate-spin mr-1.5" />Recalculating…</>
                    : <><RefreshCw size={13} className="mr-1.5" />Recalculate (×2)</>
                  }
                </Button>
                {(recalcResult || plan.recalc_1_sha) && (
                  <RecalcBadge
                    stable={recalcResult?.stable ?? plan.recalc_stable}
                    sha1={recalcResult?.recalc_1_sha ?? plan.recalc_1_sha}
                    sha2={recalcResult?.recalc_2_sha ?? plan.recalc_2_sha}
                  />
                )}
              </div>
            )}

            {step === 2 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(1)}
                  data-testid="s4-back-to-step1"
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  data-testid="s4-to-review-btn"
                  onClick={() => setStep(3)}
                >
                  Review Rows
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Step 3: Row review ───────────────────────────────────────────── */}
      {step >= 3 && planId && (
        <Card data-testid="s4-step3-review">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList size={16} /> PLU Row Review
              {step === 3 && <Badge variant="outline" className="text-xs ml-2">Current Step</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">
              Click any row to expand till breakdown and source provenance.
              Importable rows will be posted as SaleLines. Held rows are excluded from import.
            </p>
            <PluRowsTable
              planId={planId}
              category={rowCategory}
              setCategory={setRowCategory}
            />

            {step === 3 && !isApplied && (
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(2)}
                  data-testid="s4-back-to-bridge"
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  data-testid="s4-apply-btn"
                  onClick={() => setStep(4)}
                >
                  Proceed to Apply
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Step 4: Apply ────────────────────────────────────────────────── */}
      {step >= 4 && plan && (
        <Card data-testid="s4-step4-apply">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert size={16} /> Apply Stage 4
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isApplied && !applyResult && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <div className="font-semibold mb-1">Pre-Apply Checklist</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Apply triggers a double recalculation — both runs must match stored rows.</li>
                  <li>Only importable rows are posted ({plan.bridge?.importable_rows ?? '?'} rows, {fmtGBP(plan.bridge?.importable_target_value)} target).</li>
                  <li>Held rows remain in staging (not posted).</li>
                  <li>Allowances are tracked but held for Stage 6.</li>
                  <li>Repeat apply is safe (idempotent).</li>
                  <li>Site guard: only site 271 is permitted.</li>
                </ul>
              </div>
            )}

            {applyResult && <AppliedPanel applyResult={applyResult} />}

            {!isApplied && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(3)}
                  data-testid="s4-back-to-review"
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  data-testid="s4-confirm-apply-btn"
                  disabled={applying}
                  onClick={handleApply}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {applying
                    ? <><Loader2 size={14} className="animate-spin mr-2" />Applying…</>
                    : 'Confirm Apply'
                  }
                </Button>
              </div>
            )}

            {isApplied && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="s4-repeat-apply-btn"
                  disabled={applying}
                  onClick={handleApply}
                >
                  {applying
                    ? <><Loader2 size={14} className="animate-spin mr-2" />Checking…</>
                    : <><RefreshCw size={13} className="mr-1.5" />Repeat Apply (Idempotency Check)</>
                  }
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
